/**
 * Offline Architectural Capture Sync (v1.2.69)
 * =============================================
 * Drains the offlineArchitecturalQueue when connectivity returns by
 * POSTing each queued batch to /api/site-review/analyze (the SAME
 * endpoint the online Architectural Capture flow uses). Returned
 * findings are then persisted via /api/site-review/bulk-save so they
 * show up as observations on the assessment — same terminal state as
 * an online run.
 *
 * ⚠️  ISOLATION CONTRACT — DO NOT CHANGE
 * - Reads/writes ONLY services/offlineArchitecturalQueue.js.
 * - Does NOT touch services/offlineStorage.js (asset photos),
 *   services/offlineObservationQueue.js, or services/syncManager.js.
 * - Uses its own event subscribers. Own online listener. Own toasts.
 * - Bootstrap: initArchitecturalSync() called ONCE from App.js
 *   alongside the existing initObservationSync() call — no other
 *   integration.
 */

import axios from 'axios';
import { toast } from 'sonner';
import { publishObservationsChanged } from './offlineObservationSync';
import {
  getPendingArchitecturalCaptures,
  updateCaptureStatus,
  deleteCapture,
} from './offlineArchitecturalQueue';
// v1.3.16 — Manual airplane mode integration. See lib/connectivity.js.
// The `Uses its own event subscribers` isolation clause above stays
// true — this file still owns its own subscriber; it just consults
// the shared connectivity source of truth instead of the raw
// browser event so the manual toggle is honored.
import { isEffectivelyOnline, subscribeConnectivity } from '../lib/connectivity';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_RETRIES = 3;

let _draining = false;
const _listeners = new Set();

export const subscribeArchitecturalSync = (cb) => {
  _listeners.add(cb);
  return () => _listeners.delete(cb);
};

const _notify = (evt) => {
  for (const cb of _listeners) {
    try { cb(evt); } catch (_) { /* isolated listener */ }
  }
};

/**
 * Drain the architectural capture queue.
 * Safe to call repeatedly — early-outs when already draining.
 */
export const drainArchitecturalQueue = async () => {
  if (_draining) return { drained: 0, skipped_already_draining: true };
  const token = localStorage.getItem('token');
  if (!token) return { drained: 0, skipped_no_token: true };

  let pending;
  try {
    pending = await getPendingArchitecturalCaptures();
  } catch (e) {
    console.warn('[architectural-sync] Could not read queue:', e);
    return { drained: 0, error: String(e) };
  }
  if (!pending.length) return { drained: 0 };

  _draining = true;
  _notify({ type: 'started', total: pending.length });
  let ok = 0;
  let failed = 0;

  try {
    for (const row of pending) {
      // v1.3.37.146 — vendor captures drain through /vendor/architectural-captures.
      if (row.owner === 'vendor') continue;
      if (row.retry_count >= MAX_RETRIES) {
        // Keep the row but skip it — the assessor can retry manually
        // (or we'll add a review UI later).
        continue;
      }

      try {
        await updateCaptureStatus(row.id, 'uploading');

        // 1. Run the AI on the queued photos (Vertex sees sanitized
        //    bytes automatically because /site-review/analyze is
        //    wired through the PII guard as of v1.2.68).
        const { data: analyzeResp } = await axios.post(
          `${API}/site-review/analyze`,
          {
            assessment_id: row.assessment_id,
            images_base64: row.images_base64,
            context: row.context || undefined,
            location: row.location || undefined,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 180000,
          },
        );

        // 2. Persist the returned findings as observations, same as
        //    the online Save All button does. If bulk-save fails, we
        //    still consider the AI step done — a UI later can offer
        //    the assessor a chance to review + re-save.
        const findings = analyzeResp?.findings || [];
        if (findings.length) {
          try {
            await axios.post(
              `${API}/site-review/bulk-save`,
              {
                assessment_id: row.assessment_id,
                findings,
                photos_base64: row.images_base64,
              },
              {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 120000,
              },
            );
          } catch (bulkErr) {
            console.warn('[architectural-sync] bulk-save failed:', bulkErr);
          }
        }

        await deleteCapture(row.id);
        ok += 1;
        _notify({ type: 'progress', done: ok + failed, total: pending.length });
      } catch (err) {
        failed += 1;
        const msg = err?.response?.data?.detail || err?.message || 'Unknown error';
        console.warn(`[architectural-sync] Batch ${row.id} failed:`, msg);
        await updateCaptureStatus(row.id, 'failed', {
          retry_count: (row.retry_count || 0) + 1,
          last_error: String(msg).slice(0, 500),
        }).catch(() => {});
      }
    }
  } finally {
    _draining = false;
  }

  if (ok > 0) {
    toast.success(
      `Synced ${ok} Architectural Capture${ok === 1 ? '' : 's'} — findings added as observations.`,
    );
  }
  if (failed > 0) {
    toast.warning(
      `${failed} Architectural Capture${failed === 1 ? '' : 's'} failed to sync — will retry.`,
    );
  }
  _notify({ type: 'finished', ok, failed });

  // v1.2.96.10 — Announce that the drain completed so any listening
  // page (currently AssessmentPage) can refetch observations + the
  // pending-arch tray without a browser reload. Product owner:
  // 'after it runs and connects ... you see nothing of the
  // architectural findings until a hard refresh of your browser.'
  if (typeof window !== 'undefined' && (ok > 0 || failed > 0)) {
    try {
      window.dispatchEvent(
        new CustomEvent('fmiq:architectural-synced', { detail: { ok, failed } }),
      );
    } catch (e) {
      console.warn('[architectural-sync] event dispatch failed:', e);
    }
  }
  // v1.2.96.10 — Also nudge the shared observations pub/sub so the
  // ObservationsSection (which owns the observations list) auto-
  // refetches immediately. Arch findings persist as observations
  // via /site-review/bulk-save, so this is the correct channel.
  if (ok > 0) {
    try { publishObservationsChanged(); } catch { /* non-fatal */ }
  }

  return { drained: ok, failed };
};

let _initialized = false;
export const initArchitecturalSync = () => {
  if (_initialized) return;
  _initialized = true;

  const onOnline = () => {
    setTimeout(() => {
      drainArchitecturalQueue().catch((e) =>
        console.warn('[architectural-sync] drain error:', e),
      );
    }, 500);
  };

  // v1.3.16 — Subscribe to the combined connectivity signal so
  // manual-airplane-off also triggers the drain.
  subscribeConnectivity(({ effectiveOnline }) => {
    if (effectiveOnline) onOnline();
  });

  // Also drain immediately if we come online while the page is loaded.
  if (isEffectivelyOnline()) {
    onOnline();
  }
};
